import React, { useState } from 'react';
import {
  Heart,
  Quote,
  BookOpen,
  Users,
  Target,
  MessageSquare,
  BarChart3,
  FileText,
  Presentation,
  Smartphone,
  CreditCard,
  Maximize2
} from 'lucide-react';
import { LadderOfInference } from '../components/LadderOfInference';

export const VisualVerbalIntegration: React.FC = () => {
  const [selectedPurpose, setSelectedPurpose] = useState<'educational' | 'coaching' | 'organizational' | 'personal'>('educational');
  const [selectedFormat, setSelectedFormat] = useState<'slide' | 'web' | 'mobile' | 'print' | 'field-card' | 'poster'>('web');

  const purposes = [
    { 
      id: 'educational', 
      name: 'Educational', 
      icon: <BookOpen className="h-5 w-5" />, 
      description: 'Teaching and learning contexts',
      color: 'primary'
    },
    { 
      id: 'coaching', 
      name: 'Coaching', 
      icon: <Users className="h-5 w-5" />, 
      description: 'One-on-one development sessions',
      color: 'sage'
    },
    { 
      id: 'organizational', 
      name: 'Organizational', 
      icon: <Target className="h-5 w-5" />, 
      description: 'Team and business applications',
      color: 'amber'
    },
    { 
      id: 'personal', 
      name: 'Personal Development', 
      icon: <Heart className="h-5 w-5" />, 
      description: 'Individual reflection and growth',
      color: 'luminous-coral'
    }
  ];

  const formats = [
    { id: 'slide', name: 'PowerPoint Slide', icon: <Presentation className="h-5 w-5" />, description: 'Presentation format' },
    { id: 'web', name: 'Web Page', icon: <BarChart3 className="h-5 w-5" />, description: 'Interactive web experience' },
    { id: 'mobile', name: 'Mobile App', icon: <Smartphone className="h-5 w-5" />, description: 'Touch-optimized interface' },
    { id: 'print', name: 'Print Document', icon: <FileText className="h-5 w-5" />, description: 'High-resolution print format' },
    { id: 'field-card', name: 'Field Card', icon: <CreditCard className="h-5 w-5" />, description: 'Quick reference card' },
    { id: 'poster', name: 'Poster', icon: <Maximize2 className="h-5 w-5" />, description: 'Large format display' }
  ];

  const getPurposeContent = () => {
    switch (selectedPurpose) {
      case 'educational':
        return {
          title: 'Educational Application',
          subtitle: 'Teaching the Ladder of Inference in Academic Settings',
          intro: 'In educational contexts, the Ladder of Inference serves as a powerful tool for teaching critical thinking and decision-making skills.',
          keyPoints: [
            'Introduces students to cognitive biases and how they form',
            'Provides a structured framework for analyzing thought processes',
            'Encourages evidence-based reasoning and questioning assumptions',
            'Builds skills for academic research and argumentation'
          ],
          quote: '"We don\'t see things as they are, we see them as we are." — Anaïs Nin',
          example: 'A professor uses the ladder to help students analyze historical events, separating documented facts from historical interpretations and modern biases.',
          visualApproach: 'Detailed, step-by-step progression with examples and counter-examples',
          textApproach: 'Academic tone with clear definitions, examples, and reflective questions'
        };
      case 'coaching':
        return {
          title: 'Coaching Application',
          subtitle: 'One-on-One Development and Self-Reflection',
          intro: 'Coaches use the Ladder of Inference to help clients understand their thought patterns and make more conscious decisions.',
          keyPoints: [
            'Helps clients recognize automatic thought patterns',
            'Encourages self-awareness and emotional intelligence',
            'Provides tools for better communication and relationships',
            'Supports goal-setting and behavior change'
          ],
          quote: '"The Ladder of Inference teaches you the importance of stepping back, questioning your thought processes, and ensuring that your actions are grounded in a balanced and comprehensive view of reality."',
          example: 'A coach helps a client who interprets a colleague\'s comment as dismissive, working through the ladder to examine assumptions and find more constructive responses.',
          visualApproach: 'Personal, reflective format with space for client notes and insights',
          textApproach: 'Conversational tone with coaching questions and personal reflection prompts'
        };
      case 'organizational':
        return {
          title: 'Organizational Application',
          subtitle: 'Team Decision-Making and Business Strategy',
          intro: 'Organizations use the Ladder of Inference to improve team dynamics, decision-making processes, and strategic planning.',
          keyPoints: [
            'Reduces conflict by making reasoning processes explicit',
            'Improves team communication and collaboration',
            'Enhances data-driven decision making',
            'Supports organizational learning and development'
          ],
          quote: '"As leaders, we need to step back and analyze assumptions, biases, and judgments to understand how we arrived at our conclusions and to look for gaps in our thinking." — Amy Edmondson',
          example: 'A team uses the ladder to analyze customer feedback data, ensuring they base product decisions on facts rather than assumptions about user needs.',
          visualApproach: 'Professional, data-focused format with business examples and metrics',
          textApproach: 'Business tone with case studies, metrics, and actionable frameworks'
        };
      case 'personal':
        return {
          title: 'Personal Development Application',
          subtitle: 'Individual Growth and Self-Improvement',
          intro: 'Individuals use the Ladder of Inference for personal reflection, relationship improvement, and life decision-making.',
          keyPoints: [
            'Increases self-awareness and emotional intelligence',
            'Improves personal relationships and communication',
            'Supports better life decisions and goal achievement',
            'Reduces stress and conflict in personal situations'
          ],
          quote: '"Understanding this model can help improve communication, decision-making, and problem-solving by making us more aware of our cognitive biases and encouraging us to base our actions on a more comprehensive and objective view of reality."',
          example: 'Someone uses the ladder to examine why they feel defensive in certain conversations, discovering underlying assumptions that affect their relationships.',
          visualApproach: 'Intimate, reflective format with personal space for journaling and insights',
          textApproach: 'Personal, encouraging tone with self-reflection exercises and life examples'
        };
      default:
        return purposes[0];
    }
  };

  const getFormatSpecificContent = () => {
    switch (selectedFormat) {
      case 'slide':
        return {
          visualDensity: 'minimal',
          textDensity: 'concise',
          layout: 'single-concept focus',
          interaction: 'presentation-driven',
          example: 'PowerPoint slide with key ladder steps, one main quote, and 3-4 bullet points for discussion'
        };
      case 'web':
        return {
          visualDensity: 'standard',
          textDensity: 'comprehensive',
          layout: 'multi-section with navigation',
          interaction: 'clickable elements and detailed explanations',
          example: 'Interactive web page with expandable sections, multiple examples, and embedded videos'
        };
      case 'mobile':
        return {
          visualDensity: 'minimal',
          textDensity: 'essential',
          layout: 'stacked, touch-friendly',
          interaction: 'swipe navigation and tap interactions',
          example: 'Mobile app with step-by-step ladder, quick reflection prompts, and shareable insights'
        };
      case 'print':
        return {
          visualDensity: 'detailed',
          textDensity: 'comprehensive',
          layout: 'multi-column with references',
          interaction: 'static with reading flow',
          example: 'Print document with full ladder explanation, case studies, and reference materials'
        };
      case 'field-card':
        return {
          visualDensity: 'minimal',
          textDensity: 'essential',
          layout: 'compact reference',
          interaction: 'quick lookup',
          example: 'Credit card-sized reference with ladder steps, key questions, and quick tips'
        };
      case 'poster':
        return {
          visualDensity: 'visual impact',
          textDensity: 'headlines only',
          layout: 'large format display',
          interaction: 'visual scanning',
          example: 'Wall poster with large ladder visual, key quote, and inspiring message'
        };
      default:
        return { visualDensity: 'standard', textDensity: 'comprehensive', layout: 'balanced', interaction: 'moderate' };
    }
  };

  const purposeContent = getPurposeContent();
  const formatContent = getFormatSpecificContent();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <MessageSquare className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Visual-Verbal Integration</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-4xl mx-auto">
          Demonstrating how Peak Priorities combines visual grammar with verbal communication to create purpose-driven, format-adaptive content that serves different audiences and contexts.
        </p>
      </div>

      {/* Purpose Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Purpose & Context</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {purposes.map((purpose) => (
            <button
              key={purpose.id}
              onClick={() => setSelectedPurpose(purpose.id as any)}
              className={`p-4  border-2 transition-all text-left ${
                selectedPurpose === purpose.id
                  ? `border-${purpose.color}-600 bg-${purpose.color}-50 text-${purpose.color}-800`
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                {purpose.icon}
                <span className="font-semibold">{purpose.name}</span>
              </div>
              <p className="text-sm opacity-75">{purpose.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Format Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Format & Medium</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {formats.map((format) => (
            <button
              key={format.id}
              onClick={() => setSelectedFormat(format.id as any)}
              className={`p-4  border-2 transition-all text-left ${
                selectedFormat === format.id
                  ? 'border-consulting-slate-blue-600 bg-consulting-slate-blue-50 text-consulting-slate-blue-800'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                {format.icon}
                <span className="font-semibold">{format.name}</span>
              </div>
              <p className="text-sm opacity-75">{format.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Content Strategy */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Content Strategy</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Purpose Content */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">{purposeContent.title}</h3>
            <p className="text-charcoal-700 italic">{purposeContent.subtitle}</p>
            <p className="text-charcoal-600">{purposeContent.intro}</p>
            
            <div className="bg-[var(--surface)] p-4">
              <div className="flex items-start gap-3">
                <Quote className="h-5 w-5 text-[var(--mustard)] mt-1 flex-shrink-0" />
                <p className="text-charcoal-700 italic">{purposeContent.quote}</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-charcoal-900 mb-2">Key Applications:</h4>
              <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                {purposeContent.keyPoints.map((point: string, index: number) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Format Content */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Format Specifications</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-charcoal-900">Visual Density:</span>
                <p className="text-charcoal-600">{formatContent.visualDensity}</p>
              </div>
              <div>
                <span className="font-semibold text-charcoal-900">Text Density:</span>
                <p className="text-charcoal-600">{formatContent.textDensity}</p>
              </div>
              <div>
                <span className="font-semibold text-charcoal-900">Layout:</span>
                <p className="text-charcoal-600">{formatContent.layout}</p>
              </div>
              <div>
                <span className="font-semibold text-charcoal-900">Interaction:</span>
                <p className="text-charcoal-600">{formatContent.interaction}</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-charcoal-900 mb-2">Example:</h4>
              <p className="text-charcoal-600 text-sm">{formatContent.example}</p>
            </div>

            <div>
              <h4 className="font-semibold text-charcoal-900 mb-2">Visual Approach:</h4>
              <p className="text-charcoal-600 text-sm">{purposeContent.visualApproach}</p>
            </div>

            <div>
              <h4 className="font-semibold text-charcoal-900 mb-2">Text Approach:</h4>
              <p className="text-charcoal-600 text-sm">{purposeContent.textApproach}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Live Example */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-charcoal-900">Live Example</h2>
          <div className="flex items-center gap-2 text-sm text-charcoal-600">
            <span>Purpose: {selectedPurpose}</span>
            <span>•</span>
            <span>Format: {selectedFormat}</span>
          </div>
        </div>
        
        <div className="border-2 border-dashed border-[var(--rule)] p-6 bg-[var(--surface)]">
          <LadderOfInference
            format={selectedFormat}
            variant="vertical"
            contentDensity={formatContent.visualDensity === 'minimal' ? 'minimal' : 'standard'}
            interactive={selectedFormat === 'web'}
            showReflection={selectedFormat !== 'field-card'}
          />
        </div>
      </div>

      {/* Integration Principles */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-8">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-6">Visual-Verbal Integration Principles</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h4 className="font-semibold text-charcoal-900 mb-3">Visual Grammar Application</h4>
            <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
              <li><strong>Purpose-Driven Design:</strong> Visual elements support the specific learning or application goal</li>
              <li><strong>Format Optimization:</strong> Visual density and complexity adapt to the medium and context</li>
              <li><strong>Consistent Semantics:</strong> Color, shape, and typography maintain meaning across formats</li>
              <li><strong>Progressive Disclosure:</strong> Information reveals appropriately for the format and purpose</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-charcoal-900 mb-3">Verbal Communication Strategy</h4>
            <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
              <li><strong>Audience-Appropriate Tone:</strong> Language matches the context and user needs</li>
              <li><strong>Content Density Matching:</strong> Text complexity aligns with visual complexity</li>
              <li><strong>Interactive Elements:</strong> Questions and prompts support the learning objective</li>
              <li><strong>Contextual Examples:</strong> Real-world applications relevant to the purpose</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Real-World Applications */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Real-World Applications</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 border-2 border-[var(--rule)]">
            <h3 className="font-semibold text-charcoal-900 mb-3">Microsoft Case Study</h3>
            <p className="text-charcoal-600 text-sm mb-3">
              "At Microsoft, I had to learn how to clearly separate facts and data from interpretation. It sounds like a small step, but it's that translation layer that we automatically do that can get us in trouble."
            </p>
            <p className="text-charcoal-600 text-sm">
              <strong>Application:</strong> Team training on email communication and conflict resolution using the ladder to distinguish between facts and interpretations.
            </p>
          </div>

          <div className="p-6 border-2 border-[var(--rule)]">
            <h3 className="font-semibold text-charcoal-900 mb-3">Harvard Business School</h3>
            <p className="text-charcoal-600 text-sm mb-3">
              "Our brains are programmed to take shortcuts to accelerate decision-making. While we might be aware that jumping to conclusions is not the best way to make a decision, telling people not to do so is unhelpful."
            </p>
            <p className="text-charcoal-600 text-sm">
              <strong>Application:</strong> Executive education programs teaching data-driven decision making and reducing cognitive biases in business contexts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};




